import Vue from 'vue'
import VueGtag, { optIn, optOut } from 'vue-gtag'

import Config from '@/app.config'
import Logger from '@/logger'
import Router from '@/router'

import SchemeServicingManager from '@/scheme-servicing-manager'
import CookieConsentManager from '@/cookie-consent-manager'

import Hubspot, { enableHubspot, disableHubspot } from '@/plugins/Hubspot'

/**
 * @type {string}
 */
const LOG_NAME = 'Analytics'

/**
 * @returns {boolean}
 */
function isProduction() {
    return process.env.NODE_ENV === 'production'
}

// NOTE: This does not work at the scheme level because state is not available at this point.
export default class AnalyticsLoader {

    /**
     * Creates an instance of AnalyticsLoader.
     * @memberof AnalyticsLoader
     */
    constructor() {
        const schemeMap = SchemeServicingManager.getSchemeMap()

        this.googleAnalyticsId = !isProduction()
            ? Config.analyticsIds.dev
            : schemeMap && schemeMap.analyticsId ? schemeMap.analyticsId : Config.analyticsIds.default

        this.useCrmTracking = schemeMap.useCrmTracking && isProduction()

        this.linkers = !isProduction()
            ? [ 'localhost' ]
            : schemeMap && schemeMap.linkers ? schemeMap.linkers : []

        Logger.logObject({ schemeMap, gaId: this.analyticsId, linkers: this.linkers }, 'Analytics Mapped', LOG_NAME)
    }

    /**
     * Loads the analytics plugin into the Vue instance if an analytics ID is set.
     * @async
     * @memberof AnalyticsLoader
     */
    async loadAsync() {
        if (this.googleAnalyticsId)
            await this._loadGoogleAnalyticsAsync()
        else
            Logger.log('Googgle Analytics load aborted for scheme: No Analytics ID provided', LOG_NAME)

        if (this.useCrmTracking)
            await this._loadHubspotAsync()
        else
            Logger.log('Hubspot Analytics load aborted for scheme: CRM Tracking disabled', LOG_NAME)

    }

    async _loadGoogleAnalyticsAsync() {
        Logger.log('Starting Google Analytics', LOG_NAME)

        const cookieConsent = await CookieConsentManager.getConsentDataAsync()

        // noinspection JSUnusedGlobalSymbols
        Vue.use(VueGtag, {
            config: {
                id: this.googleAnalyticsId,
                params: {
                    linker: this.linkers,
                },
            },
            enabled: cookieConsent.analytics,
            pageTrackerExcludedRoutes: [],
            onReady() {
                Logger.log(`Google Analytics active tracking to ${ this.analyticsId }`, LOG_NAME)
            },
            onBeforeTrack() {
                Logger.log('Google Analytics: Sending page load', LOG_NAME)
            },
            onAfterTrack() {
                Logger.log('Google Analytics: Page load sent', LOG_NAME)
            },
        }, Router)
    }

    async _loadHubspotAsync() {
        Logger.log('Starting Hubspot Analytics', LOG_NAME)

        const cookieConsent = await CookieConsentManager.getConsentDataAsync()

        Vue.use(Hubspot, {
            enabled: cookieConsent.analytics,
        }, Router)
    }
}

/**
 * Opt in to analytics if enable is true. Otherwise opt out.
 * @param {boolean} enable
 */
export const updateAnalyticsState = enable => {
    if (enable) {
        optIn()
        enableHubspot()
    } else {
        optOut()
        disableHubspot()
    }
}
